// React dependencies
import React, { useEffect, useState } from 'react';

// External dependencies
import { IconButton } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Internal dependencies
import Colours from '../../../../../styles/colours';
import ProgressiveImage from '../../../../../components/ProgressiveImage';
import Icons from '../../../../../components/Common/Icons';
import { IsEmpty } from '../../../../../common/checks';
import MText from '../../../../../components/Common/MText';
import MapLocationSelect from '../Poi/MapLocationSelect';
import PoiMarkerIcon from '../Poi/PoiMarkerIcon';
import PhotoLocationIcon from '../PhotoLocationIcon';
import MaterialDatePicker from '../../../../../components/Common/form/MaterialDatePicker';
import MTextField from '../../../../../components/MTextField';
import PoiTags from '../../PoiTags';
import PickPoiLocationModal from '../Poi/PickPoiLocationModal';
import SelectPhotos from '../../../../../common/selectPhotos';
import SameAsPreviousAlert from './SameAsPreviousAlert';
import { MAX_FILE_SIZE, MAX_FILES } from '../SnapstyrConstants';

const TILE_SIZE = 72;

const PhotoInfo = (props) => {
  const [showSelectLocInMapModal, setShowSelectLocInMapModal] = useState(false);
  const [showSamePreviousAlert, setShowSamePreviousAlert] = useState(false);

  const onDeletePhoto = (photoIndex) => {
    const newPhotos = props.photos.filter((ph, idx) => photoIndex !== idx);
    props.onPhotosChange(newPhotos);

    if (props.selectedPhotoIdx >= photoIndex) {
      props.onSelectedPhotoIdxChange((prev) => Math.max(prev - 1, 0));
    }
  };

  const onPhotoDescriptionChange = (idx, description) => {
    const newPhotos = props.photos.map((photo, i) => (
      i === idx ? { ...photo, description } : photo
    ));
    props.onPhotosChange(newPhotos);
  };

  const onPhotoTakenOnChange = (idx, takenOn) => {
    const newPhotos = props.photos.map((photo, i) => (
      i === idx ? { ...photo, takenOn } : photo
    ));
    props.onPhotosChange(newPhotos);
  };

  const onSelectTag = (idx, tag) => {
    const newPhotos = props.photos.map((photo, i) => (
      i === idx ? { ...photo, tags: [...photo.tags, tag] } : photo
    ));
    props.onPhotosChange(newPhotos);
  };

  const onTagsChange = (idx, tags) => {
    const newPhotos = props.photos.map((photo, i) => (
      i === idx ? { ...photo, tags } : photo
    ));
    props.onPhotosChange(newPhotos);
  };

  const onPhotoLocationChange = (idx, location) => {
    const newPhotos = props.photos.map((photo, i) => (
      i === idx ? { ...photo, location } : photo
    ));
    props.onPhotosChange(newPhotos);
    setShowSamePreviousAlert(false);
  };

  const onUseSamePreviousLocation = () => {
    const previousLocation = props.photos[props.selectedPhotoIdx - 1]?.location;
    if (!IsEmpty(previousLocation)) {
      onPhotoLocationChange(props.selectedPhotoIdx, previousLocation);
      setShowSamePreviousAlert(false);
    }
  };

  const onMapClicked = () => {
      setShowSelectLocInMapModal(true);
  };

  const onAddPhoto = async () => {
    const files = await SelectPhotos({
      isMultiple:            true,
      maxFiles:              IsEmpty(props.photos) ? MAX_FILES : MAX_FILES - props.photos.length,
      maxFileSize:           MAX_FILE_SIZE,
      onMaxFileSizeExceeded: () => {
        toast.info(`There is a limit of ${MAX_FILE_SIZE}mb per photo`);
      },
    });

    if (!IsEmpty(files)) {
      props.onPhotosChange([...props.photos, ...files.map((f) => ({
        url:         f.url,
        file:        f.file,
        location:    null,
        takenOn:     null,
        description: '',
        tags:        [],
      }))]);
    }
  };

  useEffect(() => {
    if (
      props.selectedPhotoIdx !== 0
      && IsEmpty(props.photos[props.selectedPhotoIdx]?.location)
      && !IsEmpty(props.photos[props.selectedPhotoIdx - 1]?.location)
    ) {
      setShowSamePreviousAlert(true);
    } else {
      setShowSamePreviousAlert(false);
    }
  }, [props.selectedPhotoIdx]);

  const OrangeDot = () => <img style={{ marginTop: '-0.1rem', width: '0.8rem', height: '0.8rem', backgroundColor: Colours.OrangePoiMarker, borderRadius: '0.8rem' }} />;

  return (
    <>
      <div className="d-flex flex-row">
        <MText size={10} text={`(Max ${MAX_FILES} photos, ${MAX_FILE_SIZE}mb each)`} />
      </div>
      <div className="d-flex flex-row align-self-start flex-wrap" style={{ gap: 4 }}>
        {
          props.photos.map((photo, idx) => (
            <div className="d-flex flex-column align-items-center">
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions*/}
              <div
                className="p-1"
                style={{
                  border: `3px solid ${idx === props.selectedPhotoIdx ? Colours.OrangePoiMarker : Colours.White}`,
                  cursor: 'pointer',
                }}
                onClick={() => props.onSelectedPhotoIdxChange(idx)}
              >
                <ProgressiveImage
                  image={photo.url}
                  width={TILE_SIZE}
                  height={TILE_SIZE}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              </div>

              {
                !props.disabled && (
                  <IconButton onClick={() => onDeletePhoto(idx)}>
                    <DeleteOutline style={{ color: Colours.Gray7, fontSize: 15 }} />
                  </IconButton>
                )
              }
            </div>
          ))
        }
        {
          props.photos.length < MAX_FILES && !props.disabled && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                width:     TILE_SIZE,
                height:    TILE_SIZE,
                border:    `1px solid ${Colours.LightGray3}`,
                cursor:    'pointer',
                marginTop: 6,
              }}
              onClick={onAddPhoto}
            >
              <Icons.Add fontSize="2rem" colour={Colours.Gray9} />
            </div>
          )
        }
      </div>

      {
        !IsEmpty(props.photos) && (
          <div className="d-flex flex-column">
            <div className="d-flex justify-content-center mt-3" style={{ gap: 4 }}>
              <MText size={13}>
                Select location
                {' '}
                <OrangeDot />
                {' '}
                where this photo was taken at
              </MText>
              <MText text="*" colour={Colours.Red} />
            </div>

            <MapLocationSelect
              disabled={props.disabled}
              className="mt-2"
              height="20dvh"
              displayOnly
              GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
              staticMarkersData={
                !IsEmpty(props.poiAddress)
                  ? [{ latitude: props.poiAddress?.lat, longitude: props.poiAddress?.lng }]
                  : []
              }
              staticMarkerIcon={PoiMarkerIcon}
              markerIcon={() => <PhotoLocationIcon withShadow />}
              currentCoord={props.photos[props.selectedPhotoIdx]?.location}
              onClick={onMapClicked}
              showAlert={showSamePreviousAlert}
              alertComponent={() => <SameAsPreviousAlert onUseSamePreviousLocation={onUseSamePreviousLocation} />}
            />

            <div className="d-flex flex-column justify-content-center align-items-center mt-4 pt-2">
              <MText text="Photo taken on" size={13} />
              <MaterialDatePicker
                disabled={props.disabled}
                variant="outlined"
                className="mt-1"
                maxDate={new Date()}
                value={props.photos[props.selectedPhotoIdx]?.takenOn}
                onChange={(e) => onPhotoTakenOnChange(props.selectedPhotoIdx, e)}
              />
            </div>

            <div className="w-100 d-flex flex-column justify-content-center align-items-center mt-3">
              <MText text="Photo description" size={13} />
              <MText text="(max. 1000 characters)" className="mb-1" colour={Colours.LightGray4} size={11} style={{ lineHeight: '0.9rem' }} />
              <MTextField
                className="mt-1 w-100"
                minRows={8}
                maxRows={8}
                value={props.photos[props.selectedPhotoIdx]?.description || ''}
                placeholder=""
                disabled={props.disabled}
                onChange={(e) => onPhotoDescriptionChange(props.selectedPhotoIdx, e)}
                maxCharacters={1000}
                showCharactersCount
              />
            </div>

            <div className="w-100 d-flex flex-column justify-content-center align-items-center mt-2">
              <MText text="Tags" size={13} />
              <MText text="(tap 'Enter' after each Tag)" className="mb-1" colour={Colours.LightGray4} size={11} style={{ lineHeight: '0.9rem' }} />
              <PoiTags
                userTags={props.photos[props.selectedPhotoIdx]?.tags || []}
                onSelectTag={(tag) => onSelectTag(props.selectedPhotoIdx, tag)}
                onUserTagsChange={(tags) => onTagsChange(props.selectedPhotoIdx, tags)}
                textColour={Colours.Black}
                disabled={props.disabled}
              />
            </div>

            <div className="w-100 d-flex flex-row justify-content-start">
              <MText className="mt-3" text="*required" colour={Colours.Red} />
            </div>
          </div>
        )
      }

      <PickPoiLocationModal
        GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
        open={showSelectLocInMapModal}
        markerIcon={() => <PhotoLocationIcon withShadow size="20px" />}
        existingMarker={props.photos[props.selectedPhotoIdx]?.location}
        mapCenterCoord={props.poiAddress}
        staticMarkerIcon={PoiMarkerIcon}
        staticMarkersData={
          !IsEmpty(props.poiAddress)
            ? [{ latitude: props.poiAddress?.lat, longitude: props.poiAddress?.lng }]
            : []
        }
        onClose={() => { setShowSelectLocInMapModal(false); }}
        onLocationChange={(location) => {
          onPhotoLocationChange(
            props.selectedPhotoIdx,
            !IsEmpty(location) ? { lat: location.lat, lng: location.lng } : null,
          );
        }}
      />
    </>
  );
};

PhotoInfo.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.shape({
    url:      PropTypes.string,
    location: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    description: PropTypes.string,
    takenOn:     PropTypes.shape({}),
    tags:        PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  onSelectedPhotoIdxChange: PropTypes.func.isRequired,
  selectedPhotoIdx:         PropTypes.number.isRequired,
  onPhotosChange:           PropTypes.func,
  poiAddress:               PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  disabled: PropTypes.bool,
};

PhotoInfo.defaultProps = {
  onPhotosChange: () => {},
  disabled:       false,
};

export default PhotoInfo;
